/**
 * OpenAI-compatible fetch wrapper.
 *
 * OpenAI blocks browser-direct calls (no CORS header), so OpenAI chat requests
 * must go through our same-origin backend proxy (/api/openai). xAI, Gemini and
 * any other OpenAI-compatible endpoint still send CORS headers and are called
 * directly, unchanged.
 *
 * Drop-in for `fetch(endpoint, init)` where `init` carries the provider key as
 * `Authorization: Bearer <key>`. When the endpoint is OpenAI, we:
 *   - rewrite that key to X-OpenAI-Authorization (Authorization is reserved for
 *     the app's own JWT, which fetchWithCsrf injects), and
 *   - route to the backend proxy (which also gets CSRF + app-auth via fetchWithCsrf).
 * For every other endpoint we pass through to plain fetch, byte-for-byte as before.
 */

import { fetchWithCsrf } from './fetchWithCsrf';

export function openaiCompatFetch(endpoint: string, init: RequestInit = {}): Promise<Response> {
  if (!endpoint.includes('api.openai.com')) {
    return fetch(endpoint, init);
  }

  // Move the OpenAI key off Authorization (reserved for app JWT) onto X-OpenAI-Authorization.
  const src = (init.headers ?? {}) as Record<string, string>;
  const headers: Record<string, string> = {};
  for (const [k, v] of Object.entries(src)) {
    if (k.toLowerCase() === 'authorization') headers['X-OpenAI-Authorization'] = v;
    else headers[k] = v;
  }

  return fetchWithCsrf('/api/openai/v1/chat/completions', { ...init, headers });
}
